import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, Menu, X, LogOut, CalendarCheck, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/catalog", label: "Catalog" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleSignIn = () => {
    localStorage.setItem("post_login_redirect", window.location.pathname);
    login();
  };

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-gold" : "text-cream/80 hover:text-gold"
    }`;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-ink/85 border-b border-gold/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2">
            <span className="font-serif text-2xl tracking-tight text-cream">
              Idea<span className="text-gold"> Events</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}
                data-testid={`nav-link-${l.label.toLowerCase()}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cart-button"
              onClick={() => navigate("/cart")}
              className="relative p-2 text-cream/90 hover:text-gold transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-gold text-ink text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="flex items-center gap-2 pl-2">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-gold/50 object-cover" />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-ink-700 border border-gold/50 flex items-center justify-center text-gold">
                        <User className="w-4 h-4" />
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-ink-700 border-gold/30 text-cream w-52">
                  <div className="px-2 py-1.5 text-xs text-muted2 truncate">{user.email}</div>
                  <DropdownMenuSeparator className="bg-gold/20" />
                  <DropdownMenuItem data-testid="nav-my-reservations" onClick={() => navigate("/reservations")} className="cursor-pointer focus:bg-ink-600 focus:text-gold">
                    <CalendarCheck className="w-4 h-4 mr-2" /> My Reservations
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-logout" onClick={logout} className="cursor-pointer focus:bg-ink-600 focus:text-gold">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button data-testid="nav-signin-button" onClick={handleSignIn} className="btn-gold hidden sm:inline-flex !px-6 !py-2.5">
                Sign in
              </button>
            )}

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="nav-mobile-toggle" className="md:hidden p-2 text-cream">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-ink border-gold/20 text-cream w-72">
                <div className="flex flex-col gap-6 mt-10">
                  {links.map((l) => (
                    <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
                      className="text-lg font-serif text-cream hover:text-gold">
                      {l.label}
                    </NavLink>
                  ))}
                  {!user && (
                    <button onClick={() => { setOpen(false); handleSignIn(); }} className="btn-gold mt-4">
                      Sign in
                    </button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
